import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ACCESS_GROUP_LABELS, ACCESS_ROWS, LEVELS_FOR_GROUP, levelMeaning,
  type AccessGroup, type AccessLevel, type SpecialActionRow,
} from '@project/contracts'
import { Button, Empty, Field, Modal, Notice, Panel, Select, Table, type ColumnDef } from '../../design/index.js'
import { useApi, type ChangelogEntry, type PermissionRow } from '../../app/api.js'
import { adminApi } from '../../app/admin-api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { formatDateTime } from '../../framework/format.js'
import './admin.css'

/**
 * Roles and permissions (USR-006, USR-007, USR-008, USR-009, SET1-011, SET1-013).
 *
 * Two tabs, as in the reference: the matrix — one scrolling grid with the
 * Transaction, Masters and Reports bands down the side and the business roles
 * across the top — and the special actions, the single actions a business
 * keeps to particular roles. Edits on both tabs are staged and committed
 * together by the one Save; nothing changes for anyone until it is pressed.
 */

export const LEVEL_LABELS: Readonly<Record<AccessLevel, string>> = {
  NONE: 'No access',
  VIEW: 'View',
  CREATE: 'Create',
  APPROVE: 'Approve',
  FULL_ACCESS: 'Full access',
}

const GROUPS: readonly AccessGroup[] = ['TRANSACTION', 'MASTER', 'REPORT']

/** The band titles of the reference grid. */
const BAND_WORDS: Readonly<Record<AccessGroup, string>> = { TRANSACTION: 'Transaction', MASTER: 'Masters', REPORT: 'Reports' }

/**
 * The sign-in roles. Their columns keep working server-side as the fallback
 * for people without a business role, but the reference matrix lists business
 * roles only, so they sit behind "Show sign-in roles".
 */
const SIGN_IN_ROLES: ReadonlySet<string> = new Set(['OWNER', 'ADMIN', 'MEMBER'])

type Tab = 'matrix' | 'special'
type SpecialChange = { docType: string; action: string; mode: 'ALL' | 'SPECIFIC'; roles: readonly string[] }

const keyOf = (role: string, docType: string): string => `${role}\u0000${docType}`
const specialId = (r: { docType: string; action: string }): string => `${r.docType}:${r.action}`

export const PermissionsPage = () => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [tab, setTab] = useState<Tab>('matrix')
  const [pending, setPending] = useState<ReadonlyMap<string, string>>(new Map())
  const [special, setSpecial] = useState<ReadonlyMap<string, SpecialChange>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [showingLog, setShowingLog] = useState(false)

  const matrix = useQuery({ queryKey: ['adminPermissions', api.companyId], queryFn: () => api.adminPermissions() })
  const roles = useMemo(
    () => [...new Map((matrix.data ?? []).map((r: PermissionRow) => [r.role, r.roleLabel])).entries()]
      .map(([role, label]) => ({ role, label })),
    [matrix.data],
  )

  // A Specific row with no role picked yet cannot be saved; it waits on screen.
  const incomplete = [...special.values()].some((c) => c.mode === 'SPECIFIC' && c.roles.length === 0)
  const count = pending.size + special.size

  const save = useMutation({
    mutationFn: async () => {
      if (special.size > 0) {
        client.setQueryData(['specialActions', api.companyId], await admin.saveSpecialActions([...special.values()]))
      }
      if (pending.size > 0) {
        client.setQueryData(['adminPermissions', api.companyId], await admin.savePermissions([...pending.entries()].map(([k, level]) => {
          const [role, docType] = k.split('\u0000') as [string, string]
          return { role, docType, level }
        })))
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['menu'] })
      setPending(new Map())
      setSpecial(new Map())
      setError(null)
      setSaved(true)
    },
    onError: (err) => setError(messageFor(err)),
  })

  return (
    <>
      <div className="row" style={{ marginTop: 16, justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <nav className="tabstrip" aria-label="Permissions">
          <button type="button" className={`tabstrip__tab${tab === 'matrix' ? ' tabstrip__tab--active' : ''}`}
            aria-current={tab === 'matrix' ? 'page' : undefined} onClick={() => setTab('matrix')}>
            Permissions
          </button>
          <button type="button" className={`tabstrip__tab${tab === 'special' ? ' tabstrip__tab--active' : ''}`}
            aria-current={tab === 'special' ? 'page' : undefined} onClick={() => setTab('special')}>
            Special actions
          </button>
        </nav>
        <div className="row">
          <Button kind="ghost" onClick={() => setShowingLog(true)}>Changelog</Button>
          <Button kind="ghost" disabled={count === 0 || save.isPending}
            onClick={() => { setPending(new Map()); setSpecial(new Map()) }}>
            Discard
          </Button>
          <Button kind="primary" disabled={count === 0 || incomplete || save.isPending} onClick={() => save.mutate()}>
            {count === 0 ? 'Save' : `Save ${count} change${count === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
      {error ? <div style={{ margin: '16px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      {saved && count === 0 ? <div style={{ margin: '16px 0' }}><Notice kind="ok">Saved. The new permissions apply from the next thing anyone does.</Notice></div> : null}
      {matrix.isLoading ? <Loading /> : matrix.error ? <ErrorBlock error={matrix.error} /> : tab === 'matrix' ? (
        <Matrix rows={matrix.data ?? []} pending={pending}
          onChange={(next) => { setPending(next); setSaved(false) }} />
      ) : (
        <SpecialActions roles={roles} staged={special}
          onChange={(next) => { setSpecial(next); setSaved(false) }} />
      )}
      {showingLog ? <PermissionLog roles={roles} onClose={() => setShowingLog(false)} /> : null}
    </>
  )
}

/* ---- the matrix ---------------------------------------------------------- */

export const Matrix = ({ rows, pending, onChange }: {
  rows: readonly PermissionRow[]
  pending: ReadonlyMap<string, string>
  onChange: (next: ReadonlyMap<string, string>) => void
}) => {
  const [signIn, setSignIn] = useState(false)
  const byCell = useMemo(() => new Map(rows.map((r) => [keyOf(r.role, r.docType), r])), [rows])
  const allColumns = useMemo(
    () => [...new Map(rows.map((r) => [r.role, r.roleLabel])).entries()].map(([role, label]) => ({ role, label })),
    [rows],
  )
  const business = allColumns.filter((c) => !SIGN_IN_ROLES.has(c.role))
  // With no business role yet the sign-in roles are all there is to edit.
  const showSignIn = signIn || business.length === 0
  const columns = showSignIn ? allColumns : business

  const change = (role: string, docType: string, level: string): void => {
    const next = new Map(pending)
    const stored = byCell.get(keyOf(role, docType))
    if (stored && stored.level === level) next.delete(keyOf(role, docType))
    else next.set(keyOf(role, docType), level)
    onChange(next)
  }

  return (
    <Panel
      title="Permissions"
      aside={business.length > 0 ? (
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={signIn} onChange={(e) => setSignIn(e.currentTarget.checked)} />
          Show sign-in roles (Owner, Administrator, Member)
        </label>
      ) : undefined}
    >
      <p className="helper">
        A cell left alone follows the role's default. Changes are kept on this screen until you save them.
      </p>
      <div className="matrix-scroll">
        <table className="matrix matrix--wide">
          <thead>
            <tr>
              <th scope="col" rowSpan={2}>Document Type</th>
              <th scope="colgroup" colSpan={columns.length}>Roles</th>
            </tr>
            <tr>
              {columns.map((c) => <th scope="col" key={c.role}>{c.label}</th>)}
            </tr>
          </thead>
          {GROUPS.map((group) => {
            const shown = ACCESS_ROWS.filter((r) => r.group === group && rows.some((x) => x.docType === r.key))
            if (shown.length === 0) return null
            const options = LEVELS_FOR_GROUP[group].map((value) => ({ value, label: LEVEL_LABELS[value] }))
            return (
              <tbody key={group}>
                <tr className="matrix__band">
                  <th scope="rowgroup" colSpan={columns.length + 1}>{BAND_WORDS[group]}</th>
                </tr>
                {shown.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    {columns.map((column) => {
                      const cell = byCell.get(keyOf(column.role, row.key))
                      const staged = pending.get(keyOf(column.role, row.key))
                      const level = (staged ?? cell?.level ?? 'NONE') as AccessLevel
                      return (
                        <td key={column.role} className={staged ? 'perm-cell perm-cell--changed' : 'perm-cell'}>
                          <div className="perm-cell__control">
                            <Select
                              value={level}
                              onChange={(next) => change(column.role, row.key, next)}
                              options={options}
                              aria-label={`${row.label} for ${column.label}`}
                            />
                            <span
                              className="info-tip"
                              role="img"
                              tabIndex={0}
                              title={levelMeaning(row.key, level)}
                              aria-label={levelMeaning(row.key, level)}
                            >
                              i
                            </span>
                          </div>
                          {staged ? <div className="cell-sub">not saved yet</div>
                            : cell?.configured ? null : <div className="cell-sub">default</div>}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            )
          })}
        </table>
      </div>
    </Panel>
  )
}

/* ---- changelog ----------------------------------------------------------- */

/** The reference's Permission Changelog: filter by document type and by role, then Find. */
export const PermissionLog = ({ roles, onClose }: {
  roles: ReadonlyArray<{ role: string; label: string }>
  onClose: () => void
}) => {
  const api = useApi()
  const admin = adminApi(api)
  const [docType, setDocType] = useState('')
  const [role, setRole] = useState('')
  const [applied, setApplied] = useState<{ docType: string; role: string }>({ docType: '', role: '' })

  const entries = useQuery({
    queryKey: ['permissionLog', api.companyId, applied.docType, applied.role],
    queryFn: () => admin.changelog({ entity: 'role_permission', docType: applied.docType, role: applied.role }),
  })

  const describe = (r: ChangelogEntry): string => {
    const a = r.after ?? {}
    if (r.action === 'special_action') {
      return `${String(a['action'] ?? '')}: ${a['mode'] === 'SPECIFIC' ? `only ${(a['roles'] as string[] | undefined)?.join(', ')}` : 'every role'}`
    }
    return `${String(a['role'] ?? '')} → ${LEVEL_LABELS[String(a['level']) as AccessLevel] ?? String(a['level'] ?? '')}`
  }

  const columns: ReadonlyArray<ColumnDef<ChangelogEntry>> = [
    { key: 'at', header: 'When', width: 180, render: (r) => formatDateTime(r.at) },
    { key: 'actorName', header: 'Who', width: 170 },
    { key: 'subject', header: 'Document type', width: 220,
      render: (r) => r.subjectLabel ?? String(r.after?.['docType'] ?? '—') },
    { key: 'detail', header: 'What changed', render: (r) => describe(r) },
  ]

  return (
    <Modal
      title="Permission changelog"
      subtitle="Who changed what a role may do, and when."
      wide
      actions={<Button kind="primary" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      <div className="form-grid" style={{ alignItems: 'end' }}>
        <Field label="Document type">
          <Select
            value={docType}
            onChange={setDocType}
            placeholder="Any"
            options={ACCESS_ROWS.map((r) => ({ value: r.key, label: `${r.label} · ${ACCESS_GROUP_LABELS[r.group]}` }))}
          />
        </Field>
        <Field label="Role">
          <Select value={role} onChange={setRole} placeholder="Any" options={roles.map((r) => ({ value: r.role, label: r.label }))} />
        </Field>
        <div style={{ paddingBottom: 18 }}>
          <Button kind="primary" onClick={() => setApplied({ docType, role })}>Find</Button>
        </div>
      </div>
      {entries.isLoading ? <Loading /> : null}
      {entries.error ? <ErrorBlock error={entries.error} /> : null}
      <Table
        columns={columns}
        rows={entries.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="Nothing found" body={['No permission matching these filters has been changed.']} />}
      />
    </Modal>
  )
}

/* ---- special actions ----------------------------------------------------- */

export const SpecialActions = ({ roles, staged, onChange }: {
  roles: ReadonlyArray<{ role: string; label: string }>
  staged: ReadonlyMap<string, SpecialChange>
  onChange: (next: ReadonlyMap<string, SpecialChange>) => void
}) => {
  const api = useApi()
  const admin = adminApi(api)
  const rows = useQuery({ queryKey: ['specialActions', api.companyId], queryFn: () => admin.specialActions() })

  if (rows.isLoading) return <Loading />
  if (rows.error) return <ErrorBlock error={rows.error} />

  const roleLabel = (code: string): string => roles.find((r) => r.role === code)?.label ?? code
  const current = (r: SpecialActionRow): SpecialChange => staged.get(specialId(r)) ?? r
  /** Stage a row; putting it back as stored drops it from the pending set. */
  const stage = (r: SpecialActionRow, next: SpecialChange): void => {
    const map = new Map(staged)
    const same = next.mode === r.mode && (next.mode === 'ALL'
      || (next.roles.length === r.roles.length && next.roles.every((x) => r.roles.includes(x))))
    if (same) map.delete(specialId(r))
    else map.set(specialId(r), { docType: r.docType, action: r.action, mode: next.mode, roles: next.mode === 'ALL' ? [] : next.roles })
    onChange(map)
  }

  const columns: ReadonlyArray<ColumnDef<SpecialActionRow>> = [
    { key: 'docTypeLabel', header: 'Document', width: 200 },
    { key: 'label', header: 'Special Action', width: 200,
      render: (r) => (
        <>
          <div>{r.label}</div>
          {r.available ? null : <div className="cell-sub">Not on this document yet — applies once it is</div>}
        </>
      ) },
    { key: 'baseLevel', header: 'Base Permission', width: 150, render: (r) => LEVEL_LABELS[r.baseLevel] },
    { key: 'mode', header: 'Roles Permitted', width: 190,
      render: (r) => (
        <Select
          value={current(r).mode}
          aria-label={`Who may ${r.label} on ${r.docTypeLabel}`}
          onChange={(mode) => stage(r, { ...current(r), mode: mode as 'ALL' | 'SPECIFIC' })}
          options={[{ value: 'ALL', label: 'All Roles' }, { value: 'SPECIFIC', label: 'Specific Roles' }]}
        />
      ) },
    { key: 'roles', header: 'Roles',
      render: (r) => {
        const now = current(r)
        if (now.mode === 'ALL') return <span className="muted">Anyone with the base permission</span>
        const chosen = now.roles
        return (
          <div className="stack" style={{ gap: 6 }}>
            {chosen.length > 0 ? (
              <div className="row row--wrap" style={{ gap: 6 }} aria-label="Chosen roles">
                {chosen.map((code) => (
                  <button key={code} type="button" className="chip chip--removable"
                    aria-label={`Remove ${roleLabel(code)}`}
                    onClick={() => stage(r, { ...now, roles: chosen.filter((x) => x !== code) })}>
                    {roleLabel(code)} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            ) : null}
            <Select
              value=""
              placeholder={chosen.length > 0 ? 'Add another role' : 'Pick a role'}
              aria-label={`Roles who may ${r.label} on ${r.docTypeLabel}`}
              invalid={chosen.length === 0}
              onChange={(code) => { if (code) stage(r, { ...now, roles: [...chosen, code] }) }}
              options={roles.filter((x) => !chosen.includes(x.role)).map((x) => ({ value: x.role, label: x.label }))}
            />
          </div>
        )
      } },
  ]

  return (
    <Panel title="Special actions" aside="Kept to particular roles, on top of the base permission">
      <p className="helper">
        Somebody needs the base permission on the document type before any of these is offered. Choose Specific
        Roles to keep an action to the roles you pick; the server refuses it for everyone else. Changes are kept on
        this screen until you save them.
      </p>
      <Table columns={columns} rows={rows.data ?? []} getKey={specialId} />
    </Panel>
  )
}
